import React, { useEffect, useRef, useState } from 'react';

const Philosophy = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const principles = [
    {
      title: "Ground Truth",
      text: "Every step is a conversation. Between you and the earth. Unfiltered."
    },
    {
      title: "Natural Grip",
      text: "Your body knows how to move. We remove what's in the way."
    },
    {
      title: "Minimal Interference",
      text: "Less between you and the ground means more connection, more control."
    },
    {
      title: "Designed to Disappear",
      text: "The best gear is the gear you forget you're wearing."
    }
  ];

  return (
    <section 
      ref={sectionRef}
      className="relative py-32 px-6 bg-slate-900"
      data-component-path="client/src/components/Philosophy.jsx"
      data-component-line="1:5"
      data-component-id="client/src/components/Philosophy.jsx:1:5"
    >
      <div 
        className="max-w-6xl mx-auto"
        data-component-path="client/src/components/Philosophy.jsx"
        data-component-line="2:7"
        data-component-id="client/src/components/Philosophy.jsx:2:7"
      >
        <div 
          className="grid md:grid-cols-2 gap-16 lg:gap-24"
          data-component-path="client/src/components/Philosophy.jsx"
          data-component-line="3:9"
          data-component-id="client/src/components/Philosophy.jsx:3:9"
        >
          {principles.map((principle, index) => (
            <div
              key={index}
              className={`transform transition-all duration-1000 delay-${index * 100} ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              data-component-path="client/src/components/Philosophy.jsx"
              data-component-line="4:11"
              data-component-id="client/src/components/Philosophy.jsx:4:11"
            >
              <div 
                className="border-l-2 border-slate-700 pl-6"
                data-component-path="client/src/components/Philosophy.jsx"
                data-component-line="5:13"
                data-component-id="client/src/components/Philosophy.jsx:5:13"
              >
                <h3 
                  className="text-2xl md:text-3xl font-bold text-slate-100 mb-4 tracking-tight"
                  data-component-path="client/src/components/Philosophy.jsx"
                  data-component-line="6:15"
                  data-component-id="client/src/components/Philosophy.jsx:6:15"
                >
                  {principle.title}
                </h3>
                <p 
                  className="text-lg text-slate-400 leading-relaxed font-light"
                  data-component-path="client/src/components/Philosophy.jsx"
                  data-component-line="7:15"
                  data-component-id="client/src/components/Philosophy.jsx:7:15"
                >
                  {principle.text}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Subtle texture overlay */}
      <div 
        className="absolute inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`,
        }}
        data-component-path="client/src/components/Philosophy.jsx"
        data-component-line="8:7"
        data-component-id="client/src/components/Philosophy.jsx:8:7"
      ></div>
    </section>
  );
};

export default Philosophy;